import { useMemo } from 'react';
import { type AlignedData, type Options } from 'uplot';
import { ChartColorFallback } from '@/tokens/chart-color-fallbacks';
import { ChartCanvas, readCssVar } from './ChartCanvas';

// Phase 2 (3/n) — TrendTrack.
//
// A single-series line chart over time. Powers:
//   - S1 + S3 health-history mini-trends
//   - S5 Signal Analysis trend pane (eventually with 25k points)
//
// Severity bands shade the y-axis background:
//   0..30   critical (red)
//   30..50  warn (orange)
//   50..70  watch (yellow)
//   70..100 healthy (green)
//
// The bands re-read CSS vars on each render so dark-mode flips work
// without prop drilling. ChartCanvas owns DPR + resize; TrendTrack
// only owns the options object.

export type TrendPoint = {
  /** Unix epoch in seconds. uPlot's native x-axis unit. */
  t: number;
  /** 0..100 health score. */
  v: number;
};

export type TrendTrackProps = {
  /** Points sorted ascending by `t`. Empty array → "no data" overlay. */
  points: TrendPoint[];
  /** Loading flag; ChartCanvas isn't created until points arrive. */
  loading?: boolean;
  /** Optional error message; rendered in place of the chart. */
  error?: string | null;
  /** Optional override for the y-axis range. Defaults to [0, 100]. */
  yRange?: [number, number];
  /** Aspect ratio (width / height). Default 3.0 — wide trend track. */
  aspect?: number;
  /** Test/Storybook-only: a11y label. */
  ariaLabel?: string;
};

export function TrendTrack({
  points,
  loading,
  error,
  yRange = [0, 100],
  aspect = 3.0,
  ariaLabel,
}: TrendTrackProps) {
  const { data, options } = useMemo(() => {
    if (points.length === 0) {
      return { data: null, options: null };
    }
    const xs = points.map((p) => p.t);
    const vs = points.map((p) => p.v);
    const data: AlignedData = [xs, vs];

    const colorHealthy = readCssVar('color-healthy', ChartColorFallback.healthy);
    const colorWatch = readCssVar('color-watch', ChartColorFallback.watch);
    const colorWarn = readCssVar('color-warn', ChartColorFallback.warn);
    const colorCritical = readCssVar('color-critical', ChartColorFallback.critical);
    const colorChrome = readCssVar('color-gray-700', ChartColorFallback.gray700);

    const options: Omit<Options, 'width' | 'height'> = {
      scales: {
        x: { time: true },
        y: { range: yRange },
      },
      axes: [
        { stroke: colorChrome },
        { stroke: colorChrome, grid: { stroke: 'rgba(0,0,0,0.05)' } },
      ],
      series: [
        {},
        {
          stroke: lineColorForLast(points[points.length - 1].v),
          width: 2,
          points: { show: false },
        },
      ],
      hooks: {
        drawClear: [
          (u) => {
            // Severity-band shading behind the data line.
            const ctx = u.ctx;
            const left = u.bbox.left;
            const top = u.bbox.top;
            const width = u.bbox.width;
            const height = u.bbox.height;
            const bands = [
              { lo: 0, hi: 30, fill: colorCritical },
              { lo: 30, hi: 50, fill: colorWarn },
              { lo: 50, hi: 70, fill: colorWatch },
              { lo: 70, hi: 100, fill: colorHealthy },
            ];
            ctx.save();
            ctx.globalAlpha = 0.06;
            for (const b of bands) {
              const yTop = u.valToPos(b.hi, 'y', true);
              const yBot = u.valToPos(b.lo, 'y', true);
              ctx.fillStyle = b.fill;
              ctx.fillRect(left, yTop, width, yBot - yTop);
            }
            ctx.restore();
            void height; // referenced for completeness; bbox.height drives band heights via valToPos
            void top;
          },
        ],
      },
      legend: { show: false },
      cursor: { drag: { x: false, y: false } },
    };
    return { data, options };
  }, [points, yRange]);

  if (loading) {
    return <ChartSkeleton aspect={aspect} />;
  }
  if (error) {
    return (
      <div
        className="flex h-full items-center justify-center text-fs-meta text-gray-700"
        role="alert"
      >
        {error}
      </div>
    );
  }
  if (!data || !options) {
    return <ChartEmpty aspect={aspect} />;
  }
  return (
    <ChartCanvas
      data={data}
      options={options}
      aspect={aspect}
      ariaLabel={ariaLabel}
      className="w-full"
    />
  );
}

function lineColorForLast(v: number): string {
  if (v < 30) return readCssVar('color-critical', ChartColorFallback.critical);
  if (v < 50) return readCssVar('color-warn', ChartColorFallback.warn);
  if (v < 70) return readCssVar('color-watch', ChartColorFallback.watch);
  return readCssVar('color-healthy', ChartColorFallback.healthy);
}

function ChartSkeleton({ aspect }: { aspect: number }) {
  return (
    <div
      className="w-full animate-pulse rounded-r-default bg-gray-100"
      style={{ aspectRatio: aspect }}
    />
  );
}

function ChartEmpty({ aspect }: { aspect: number }) {
  return (
    <div
      className="flex w-full items-center justify-center rounded-r-default border border-dashed border-gray-300 text-fs-meta text-gray-700"
      style={{ aspectRatio: aspect }}
    >
      No data yet.
    </div>
  );
}
